import { useGeocodingCore } from '@mapbox/search-js-react';
import { useGateValue } from '@statsig/react-bindings';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectIsGoogleMapsLoaded } from 'redux/config/config.selectors';
import { getEnv } from 'util/getEnv';

// I'm putting this in a custom hook in case we want to move mapbox to webappconfig in the future
export const useMapType = () => {
  const value = useGateValue('web-mapbox');
  const googleMapsLoaded = useSelector(selectIsGoogleMapsLoaded);

  const isLoading = useMemo(() => {
    if (!value) {
      return !googleMapsLoaded;
    }
    return false;
  }, [googleMapsLoaded, value]);

  return {
    isMapbox: value,
    isLoading,
  };
};

const MockGeocodingCore = {
  forward: async q => {
    return responses[q] || responses['121 Herons'];
  },
};

export const useGeocoding = () => {
  const isCypress = !!window.Cypress;
  const mapboxGeocoding = useGeocodingCore({
    accessToken: isCypress
      ? 'test'
      : getEnv('REACT_APP_INC_MAPBOX_ACCESS_TOKEN'),
  });

  return isCypress ? MockGeocodingCore : mapboxGeocoding;
};

const responses = {
  '121 Herons': {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
        geometry: {
          type: 'Point',
          coordinates: [-81.289194, 29.841361],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
          feature_type: 'address',
          full_address:
            '121 Herons Nest Lane, St. Augustine, Florida 32080, United States',
          name: '121 Herons Nest Lane',
          name_preferred: '121 Herons Nest Lane',
          coordinates: {
            longitude: -81.2892131,
            latitude: 29.8420765,
            accuracy: 'rooftop',
            routable_points: [
              {
                name: 'default',
                latitude: 29.841741,
                longitude: -81.289372,
              },
            ],
          },
          place_formatted: 'St. Augustine, Florida 32080, United States',
          match_code: {
            address_number: 'matched',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
              address_number: '121',
              street_name: 'Herons Nest Lane',
              name: '121 Herons Nest Lane',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHI6MDZiN2Y1YzItZjdkMy00ZjZiLWJhZjUtZDE3YmZjNGEwYTQ1',
              name: 'Herons Nest Lane',
            },
            neighborhood: {
              mapbox_id: 'dXJuOm1ieHBsYzpGeHFNN0E',
              name: 'Marsh Creek Country Club',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCazRPN0E',
              name: '32080',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpFclhJN0E',
              name: 'St. Augustine',
              wikidata_id: 'Q487988',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpBVkJHN0E',
              name: 'St. Johns County',
              wikidata_id: 'Q494471',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZVRz',
              name: 'Florida',
              wikidata_id: 'Q812',
              region_code: 'FL',
              region_code_full: 'US-FL',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6MQ',
        geometry: {
          type: 'Point',
          coordinates: [-81.621563, 30.818214],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6MQ',
          feature_type: 'address',
          full_address:
            "121 Heron's Nest Circle, Kingsland, Georgia 31548, United States",
          name: "121 Heron's Nest Circle",
          name_preferred: "121 Heron's Nest Circle",
          coordinates: {
            longitude: -81.621563,
            latitude: 30.818214,
            accuracy: 'interpolated',
            routable_points: [
              {
                name: 'default',
                latitude: 30.818214,
                longitude: -81.621563,
              },
            ],
          },
          place_formatted: 'Kingsland, Georgia 31548, United States',
          match_code: {
            address_number: 'plausible',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6MQ',
              address_number: '121',
              street_name: "Heron's Nest Circle",
              name: "121 Heron's Nest Circle",
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHItaXRwOmV5SnhJam9pTVRJeElHaGxjbTl1Y3lJc0lteHBiV2wwSWpvaU5TSXNJbkJ5YjNocGJXbDBlU0k2SWkwNE1TNDBNaXd6TUM0d01TSXNJblI1Y0dWeklqb2lZV1JrY21WemN5eHdiR0ZqWlN4d2IzTjBZMjlrWlNJc0ltbHVZMngxWkdWZmJXRjBZMmhmWkdWMFlXbHNJam9pZEhKMVpTSXNJbTF2WkdVaU9pSm1iM0ozWVhKa0lpd2lkalowZVhCbGN5STZJbUZrWkhKbGMzTXNjR3hoWTJVc2NHOXpkR052WkdVaWZROjE',
              name: "Heron's Nest Circle",
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCalN1N0E',
              name: '31548',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpDamZJN0E',
              name: 'Kingsland',
              wikidata_id: 'Q2398546',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpMY2Jz',
              name: 'Camden County',
              wikidata_id: 'Q110504',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpBcVRz',
              name: 'Georgia',
              wikidata_id: 'Q1428',
              region_code: 'GA',
              region_code_full: 'US-GA',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mg',
        geometry: {
          type: 'Point',
          coordinates: [-81.021404, 32.033713],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mg',
          feature_type: 'address',
          full_address:
            '121 Herons Nest, Savannah, Georgia 31410, United States',
          name: '121 Herons Nest',
          name_preferred: '121 Herons Nest',
          coordinates: {
            longitude: -81.021404,
            latitude: 32.033713,
            accuracy: 'interpolated',
            routable_points: [
              {
                name: 'default',
                latitude: 32.033713,
                longitude: -81.021404,
              },
            ],
          },
          place_formatted: 'Savannah, Georgia 31410, United States',
          match_code: {
            address_number: 'plausible',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mg',
              address_number: '121',
              street_name: 'Herons Nest',
              name: '121 Herons Nest',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHItaXRwOmV5SnhJam9pTVRJeElHaGxjbTl1Y3lJc0lteHBiV2wwSWpvaU5TSXNJbkJ5YjNocGJXbDBlU0k2SWkwNE1TNDBNaXd6TUM0d01TSXNJblI1Y0dWeklqb2lZV1JrY21WemN5eHdiR0ZqWlN4d2IzTjBZMjlrWlNJc0ltbHVZMngxWkdWZmJXRjBZMmhmWkdWMFlXbHNJam9pZEhKMVpTSXNJbTF2WkdVaU9pSm1iM0ozWVhKa0lpd2lkalowZVhCbGN5STZJbUZrWkhKbGMzTXNjR3hoWTJVc2NHOXpkR052WkdVaWZROjI',
              name: 'Herons Nest',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCaS9PN0E',
              name: '31410',
            },
            locality: {
              mapbox_id: 'dXJuOm1ieHBsYzpKQU9xN0E',
              name: 'Whitemarsh Island',
              wikidata_id: 'Q2575984',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpFWTVvN0E',
              name: 'Savannah',
              wikidata_id: 'Q83813',
              alternate: {
                mapbox_id: 'dXJuOm1ieHBsYzpFM2hvN0E',
                name: 'Thunderbolt',
              },
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpPWWJz',
              name: 'Chatham County',
              wikidata_id: 'Q384890',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpBcVRz',
              name: 'Georgia',
              wikidata_id: 'Q1428',
              region_code: 'GA',
              region_code_full: 'US-GA',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mw',
        geometry: {
          type: 'Point',
          coordinates: [-80.95951, 32.272245],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mw',
          feature_type: 'address',
          full_address:
            '121 Herons Bill Drive, Bluffton, South Carolina 29909, United States',
          name: '121 Herons Bill Drive',
          name_preferred: '121 Herons Bill Drive',
          coordinates: {
            longitude: -80.95951,
            latitude: 32.272245,
            accuracy: 'interpolated',
            routable_points: [
              {
                name: 'default',
                latitude: 32.272245,
                longitude: -80.95951,
              },
            ],
          },
          place_formatted: 'Bluffton, South Carolina 29909, United States',
          match_code: {
            address_number: 'plausible',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkci1pdHA6ZXlKeElqb2lNVEl4SUdobGNtOXVjeUlzSW14cGJXbDBJam9pTlNJc0luQnliM2hwYldsMGVTSTZJaTA0TVM0ME1pd3pNQzR3TVNJc0luUjVjR1Z6SWpvaVlXUmtjbVZ6Y3l4d2JHRmpaU3h3YjNOMFkyOWtaU0lzSW1sdVkyeDFaR1ZmYldGMFkyaGZaR1YwWVdsc0lqb2lkSEoxWlNJc0ltMXZaR1VpT2lKbWIzSjNZWEprSWl3aWRqWjBlWEJsY3lJNkltRmtaSEpsYzNNc2NHeGhZMlVzY0c5emRHTnZaR1VpZlE6Mw',
              address_number: '121',
              street_name: 'Herons Bill Drive',
              name: '121 Herons Bill Drive',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHItaXRwOmV5SnhJam9pTVRJeElHaGxjbTl1Y3lJc0lteHBiV2wwSWpvaU5TSXNJbkJ5YjNocGJXbDBlU0k2SWkwNE1TNDBNaXd6TUM0d01TSXNJblI1Y0dWeklqb2lZV1JrY21WemN5eHdiR0ZqWlN4d2IzTjBZMjlrWlNJc0ltbHVZMngxWkdWZmJXRjBZMmhmWkdWMFlXbHNJam9pZEhKMVpTSXNJbTF2WkdVaU9pSm1iM0ozWVhKa0lpd2lkalowZVhCbGN5STZJbUZrWkhKbGMzTXNjR3hoWTJVc2NHOXpkR052WkdVaWZROjM',
              name: 'Herons Bill Drive',
            },
            neighborhood: {
              mapbox_id: 'dXJuOm1ieHBsYzpKV0FzN0E',
              name: 'Sun City Hilton Head',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZEt1N0E',
              name: '29909',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpBZWJJN0E',
              name: 'Bluffton',
              wikidata_id: 'Q3243593',
              alternate: {
                mapbox_id: 'dXJuOm1ieHBsYzpHMnpvN0E',
                name: 'Okatie',
              },
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpFNmJz',
              name: 'Beaufort County',
              wikidata_id: 'Q495682',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpBd1Rz',
              name: 'South Carolina',
              wikidata_id: 'Q1456',
              region_code: 'SC',
              region_code_full: 'US-SC',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjoxNWZhNWMzYy0xZDE4LTRlOTQtYmY4ZC05OTI0OWM3YjQ2OGE',
        geometry: {
          type: 'Point',
          coordinates: [-81.04226, 27.35175],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjoxNWZhNWMzYy0xZDE4LTRlOTQtYmY4ZC05OTI0OWM3YjQ2OGE',
          feature_type: 'address',
          full_address: '121 Herons Way, Lorida, Florida 33857, United States',
          name: '121 Herons Way',
          name_preferred: '121 Herons Way',
          coordinates: {
            longitude: -81.04226,
            latitude: 27.35175,
            accuracy: 'point',
            routable_points: [
              {
                name: 'default',
                latitude: 27.351637,
                longitude: -81.042263,
              },
            ],
          },
          place_formatted: 'Lorida, Florida 33857, United States',
          match_code: {
            address_number: 'matched',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkcjoxNWZhNWMzYy0xZDE4LTRlOTQtYmY4ZC05OTI0OWM3YjQ2OGE',
              address_number: '121',
              street_name: 'Herons Way',
              name: '121 Herons Way',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHI6MTVmYTVjM2MtMWQxOC00ZTk0LWJmOGQtOTkyNDljN2I0Njhh',
              name: 'Herons Way',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCdE5PN0E',
              name: '33857',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpDM1RvN0E',
              name: 'Lorida',
              wikidata_id: 'Q6681173',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpub2Jz',
              name: 'Highlands County',
              wikidata_id: 'Q488885',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZVRz',
              name: 'Florida',
              wikidata_id: 'Q812',
              region_code: 'FL',
              region_code_full: 'US-FL',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
    ],
    attribution:
      'NOTICE: © 2025 Mapbox and its suppliers. All rights reserved. Use of this data is subject to the Mapbox Terms of Service (https://www.mapbox.com/about/maps/). This response and the information it contains may not be retained.',
  },
  '121 Herons Nest Lane, St. Augustine, Florida 32080, United States': {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
        geometry: {
          type: 'Point',
          coordinates: [-81.289194, 29.841361],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
          feature_type: 'address',
          full_address:
            '121 Herons Nest Lane, St. Augustine, Florida 32080, United States',
          name: '121 Herons Nest Lane',
          name_preferred: '121 Herons Nest Lane',
          coordinates: {
            longitude: -81.2892131,
            latitude: 29.8420765,
            accuracy: 'rooftop',
            routable_points: [
              {
                name: 'default',
                latitude: 29.841741,
                longitude: -81.289372,
              },
            ],
          },
          place_formatted: 'St. Augustine, Florida 32080, United States',
          match_code: {
            address_number: 'matched',
            street: 'matched',
            postcode: 'matched',
            place: 'unmatched',
            region: 'matched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'high',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
              address_number: '121',
              street_name: 'Herons Nest Lane',
              name: '121 Herons Nest Lane',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHI6MDZiN2Y1YzItZjdkMy00ZjZiLWJhZjUtZDE3YmZjNGEwYTQ1',
              name: 'Herons Nest Lane',
            },
            neighborhood: {
              mapbox_id: 'dXJuOm1ieHBsYzpGeHFNN0E',
              name: 'Marsh Creek Country Club',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCazRPN0E',
              name: '32080',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpFclhJN0E',
              name: 'St. Augustine',
              wikidata_id: 'Q487988',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpBVkJHN0E',
              name: 'St. Johns County',
              wikidata_id: 'Q494471',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZVRz',
              name: 'Florida',
              wikidata_id: 'Q812',
              region_code: 'FL',
              region_code_full: 'US-FL',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
    ],
    attribution:
      '© 2025 Mapbox and its suppliers. All rights reserved. Use of this data is subject to the Mapbox Terms of Service. (https://www.mapbox.com/about/maps/)',
  },
  badSuggestionsNoStreetNumber: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
        geometry: {
          type: 'Point',
          coordinates: [-81.289194, 29.841361],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
          feature_type: 'address',
          full_address: 'badSuggestionsNoStreetNumber',
          name: 'Herons Nest Lane',
          name_preferred: 'Herons Nest Lane',
          coordinates: {
            longitude: -81.289194,
            latitude: 29.841361,
            accuracy: 'rooftop',
            routable_points: [
              {
                name: 'default',
                latitude: 29.841741,
                longitude: -81.289372,
              },
            ],
          },
          place_formatted: 'St. Augustine, Florida 32080, United States',
          match_code: {
            address_number: 'matched',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHI6MDZiN2Y1YzItZjdkMy00ZjZiLWJhZjUtZDE3YmZjNGEwYTQ1',
              name: 'Herons Nest Lane',
            },
            neighborhood: {
              mapbox_id: 'dXJuOm1ieHBsYzpGeHFNN0E',
              name: 'Marsh Creek Country Club',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCazRPN0E',
              name: '32080',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpFclhJN0E',
              name: 'St. Augustine',
              wikidata_id: 'Q487988',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpBVkJHN0E',
              name: 'St. Johns County',
              wikidata_id: 'Q494471',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZVRz',
              name: 'Florida',
              wikidata_id: 'Q812',
              region_code: 'FL',
              region_code_full: 'US-FL',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
    ],
    attribution:
      'NOTICE: © 2025 Mapbox and its suppliers. All rights reserved. Use of this data is subject to the Mapbox Terms of Service (https://www.mapbox.com/about/maps/). This response and the information it contains may not be retained.',
  },
  badSuggestionsMissingFields: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
        geometry: {
          type: 'Point',
          coordinates: [-81.289194, 29.841361],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
          feature_type: 'address',
          full_address: 'badSuggestionsMissingFields',
          name: 'Herons Nest Lane',
          name_preferred: 'Herons Nest Lane',
          coordinates: {
            longitude: -81.289194,
            latitude: 29.841361,
            accuracy: 'rooftop',
            routable_points: [
              {
                name: 'default',
                latitude: 29.841741,
                longitude: -81.289372,
              },
            ],
          },
          place_formatted: 'St. Augustine, Florida 32080, United States',
          match_code: {
            address_number: 'matched',
            street: 'unmatched',
            postcode: 'unmatched',
            place: 'unmatched',
            region: 'unmatched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'low',
          },
          context: {},
        },
      },
    ],
    attribution:
      'NOTICE: © 2025 Mapbox and its suppliers. All rights reserved. Use of this data is subject to the Mapbox Terms of Service (https://www.mapbox.com/about/maps/). This response and the information it contains may not be retained.',
  },
  badSuggestionsNoLatLon: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
        geometry: {
          type: 'Point',
          coordinates: [-81.289194, 29.841361],
        },
        properties: {
          mapbox_id:
            'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
          feature_type: 'address',
          full_address: 'badSuggestionsNoLatLon',
          name: '121 Herons Nest Lane',
          name_preferred: '121 Herons Nest Lane',
          place_formatted: 'St. Augustine, Florida 32080, United States',
          match_code: {
            address_number: 'matched',
            street: 'matched',
            postcode: 'matched',
            place: 'unmatched',
            region: 'matched',
            locality: 'not_applicable',
            country: 'inferred',
            confidence: 'high',
          },
          context: {
            address: {
              mapbox_id:
                'dXJuOm1ieGFkcjowNmI3ZjVjMi1mN2QzLTRmNmItYmFmNS1kMTdiZmM0YTBhNDU',
              address_number: '121',
              street_name: 'Herons Nest Lane',
              name: '121 Herons Nest Lane',
            },
            street: {
              mapbox_id:
                'dXJuOm1ieGFkci1zdHI6MDZiN2Y1YzItZjdkMy00ZjZiLWJhZjUtZDE3YmZjNGEwYTQ1',
              name: 'Herons Nest Lane',
            },
            neighborhood: {
              mapbox_id: 'dXJuOm1ieHBsYzpGeHFNN0E',
              name: 'Marsh Creek Country Club',
            },
            postcode: {
              mapbox_id: 'dXJuOm1ieHBsYzpCazRPN0E',
              name: '32080',
            },
            place: {
              mapbox_id: 'dXJuOm1ieHBsYzpFclhJN0E',
              name: 'St. Augustine',
              wikidata_id: 'Q487988',
            },
            district: {
              mapbox_id: 'dXJuOm1ieHBsYzpBVkJHN0E',
              name: 'St. Johns County',
              wikidata_id: 'Q494471',
            },
            region: {
              mapbox_id: 'dXJuOm1ieHBsYzpCZVRz',
              name: 'Florida',
              wikidata_id: 'Q812',
              region_code: 'FL',
              region_code_full: 'US-FL',
            },
            country: {
              mapbox_id: 'dXJuOm1ieHBsYzpJdXc',
              name: 'United States',
              wikidata_id: 'Q30',
              country_code: 'US',
              country_code_alpha_3: 'USA',
            },
          },
        },
      },
    ],
    attribution:
      '© 2025 Mapbox and its suppliers. All rights reserved. Use of this data is subject to the Mapbox Terms of Service. (https://www.mapbox.com/about/maps/)',
  },
};
